/**
 * SupportTicketDetail Page
 *
 * Full ticket detail view with timeline, notes, SLA tracking, and actions.
 * Provides complete CRUD operations for support tickets.
 *
 * Epic: MVP 1.21.1-TKT - Support Ticket System (SCRUM-440)
 * Week 2: Ticket Detail Page + Email Integration
 * Location: apps/web/src/pages/support/SupportTicketDetail.jsx
 *
 * Features:
 * - Full ticket information display
 * - Edit subject, description, priority, category
 * - View activity timeline with notes and status changes
 * - SLA indicator with time remaining
 * - Canned response picker for quick replies
 * - Add internal notes and public responses
 * - Assign/reassign ticket
 * - Change ticket status
 * - Related booking/user information
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Save as _Save,
  X,
  User as _User,
  Calendar,
  Clock,
  AlertCircle as _AlertCircle,
  CheckCircle,
  MessageCircle as _MessageCircle,
  FileText as _FileText,
  Mail as _Mail,
  Link as LinkIcon,
  Save,
  User,
  AlertCircle
} from 'lucide-react';
import { useSupportTicket, useUpdateTicket, useAddNote } from '../../hooks/support/useSupportTicket';
import { TicketTimeline } from '../../components/support/TicketTimeline';
import { SLAIndicator } from '../../components/support/SLAIndicator';
import { CannedResponsePicker } from '../../components/support/CannedResponsePicker';
import { AddNoteForm } from '../../components/support/AddNoteForm';

export function SupportTicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { ticket, loading, error, refresh } = useSupportTicket(id);
  const { updateTicket, updating } = useUpdateTicket(id);
  const { addNote, adding } = useAddNote(id);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    subject: '',
    description: '',
    priority: '',
    request_type: '',
  });

  // Note form state
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [noteType, setNoteType] = useState('internal'); // 'internal' | 'public'

  // Initialize edit form when ticket loads
  useEffect(() => {
    if (ticket) {
      setEditForm({
        subject: ticket.subject || '',
        description: ticket.description || '',
        priority: ticket.priority || 'medium',
        request_type: ticket.request_type || 'general_inquiry',
      });
    }
  }, [ticket]);

  const handleEditSave = async () => {
    try {
      await updateTicket(editForm);
      setIsEditing(false);
      refresh();
    } catch (err) {
      console.error('Failed to update ticket:', err);
    }
  };

  const handleEditCancel = () => {
    if (ticket) {
      setEditForm({
        subject: ticket.subject || '',
        description: ticket.description || '',
        priority: ticket.priority || 'medium',
        request_type: ticket.request_type || 'general_inquiry',
      });
    }
    setIsEditing(false);
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await updateTicket({ status: newStatus });
      refresh();
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const _handleAssign = async (userId) => {
    try {
      await updateTicket({ assigned_to: userId });
      refresh();
    } catch (err) {
      console.error('Failed to assign ticket:', err);
    }
  };

  const handleAddNote = async () => {
    if (!noteContent.trim()) {return;}

    try {
      await addNote({
        content: noteContent,
        is_internal: noteType === 'internal',
      });
      setNoteContent('');
      setShowNoteForm(false);
      refresh();
    } catch (err) {
      console.error('Failed to add note:', err);
    }
  };

  const handleUseCannedResponse = (content) => {
    setNoteContent(content);
    setNoteType('public');
    setShowNoteForm(true);
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'text-red-700 bg-red-100 border-red-300',
      high: 'text-orange-700 bg-orange-100 border-orange-300',
      medium: 'text-yellow-700 bg-yellow-100 border-yellow-300',
      low: 'text-gray-700 bg-gray-100 border-gray-300',
    };
    return colors[priority] || colors.medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-yellow-700 bg-yellow-100 border-yellow-300',
      in_progress: 'text-blue-700 bg-blue-100 border-blue-300',
      resolved: 'text-green-700 bg-green-100 border-green-300',
      closed: 'text-gray-700 bg-gray-100 border-gray-300',
    };
    return colors[status] || colors.pending;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading ticket...</p>
        </div>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Ticket</h2>
          <p className="text-gray-600 mb-6">{error || 'Ticket not found'}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate('/support')}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Back to Dashboard
            </button>
            <button
              onClick={refresh}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/support')}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-xl font-bold text-gray-900">
                    Support Request #{ticket.id.slice(0, 8)}
                  </h1>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(ticket.status)}`}>
                    {ticket.status.replace('_', ' ')}
                  </span>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${getPriorityColor(ticket.priority)}`}>
                    {ticket.priority}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {ticket.request_type.replace('_', ' ')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
              ) : (
                <>
                  <button
                    onClick={handleEditCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                  <button
                    onClick={handleEditSave}
                    disabled={updating}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save className="w-4 h-4" />
                    {updating ? 'Saving...' : 'Save'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* SLA Indicator */}
            {ticket.sla_status && ticket.sla_status.hasPolicy && (
              <SLAIndicator slaStatus={ticket.sla_status} />
            )}

            {/* Ticket Details Card */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Ticket Details</h2>

                {isEditing ? (
                  <div className="space-y-4">
                    {/* Edit Subject */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Subject
                      </label>
                      <input
                        type="text"
                        value={editForm.subject}
                        onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    {/* Edit Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        rows={6}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    {/* Edit Priority */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Priority
                      </label>
                      <select
                        value={editForm.priority}
                        onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="urgent">Urgent</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select>
                    </div>

                    {/* Edit Request Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Request Type
                      </label>
                      <select
                        value={editForm.request_type}
                        onChange={(e) => setEditForm({ ...editForm, request_type: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="booking_issue">Booking Issue</option>
                        <option value="payment_issue">Payment Issue</option>
                        <option value="account_issue">Account Issue</option>
                        <option value="technical_issue">Technical Issue</option>
                        <option value="general_inquiry">General Inquiry</option>
                        <option value="cancellation_request">Cancellation Request</option>
                        <option value="refund_request">Refund Request</option>
                        <option value="follow_up">Follow Up</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* View Subject */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Subject
                      </label>
                      <p className="text-base text-gray-900">{ticket.subject}</p>
                    </div>

                    {/* View Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {ticket.description}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Customer Information */}
              <div className="p-6 border-b border-gray-200 bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Customer Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Name
                    </label>
                    <p className="text-sm text-gray-900">{ticket.user_name || ticket.requester_name}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Email
                    </label>
                    <p className="text-sm text-gray-900">{ticket.user_email || ticket.requester_email}</p>
                  </div>
                  {ticket.related_booking_id && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Related Booking
                      </label>
                      <button
                        onClick={() => navigate(`/bookings/${ticket.related_booking_id}`)}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                      >
                        <LinkIcon className="w-3 h-3" />
                        View Booking
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Canned Responses */}
              <div className="p-6">
                <CannedResponsePicker
                  requestType={ticket.request_type}
                  onSelect={handleUseCannedResponse}
                />
              </div>
            </div>

            {/* Add Note Section */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Add Response</h3>
                {!showNoteForm && (
                  <button
                    onClick={() => setShowNoteForm(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    Add Note
                  </button>
                )}
              </div>

              {showNoteForm && (
                <AddNoteForm
                  content={noteContent}
                  noteType={noteType}
                  onContentChange={setNoteContent}
                  onNoteTypeChange={setNoteType}
                  onSubmit={handleAddNote}
                  onCancel={() => {
                    setShowNoteForm(false);
                    setNoteContent('');
                  }}
                  submitting={adding}
                />
              )}
            </div>

            {/* Timeline */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Activity Timeline</h3>
              </div>
              <TicketTimeline ticketId={ticket.id} />
            </div>
          </div>

          {/* Sidebar - Right Column */}
          <div className="space-y-6">
            {/* Status Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Actions</h3>
              <div className="space-y-2">
                {ticket.status === 'pending' && (
                  <button
                    onClick={() => handleStatusChange('in_progress')}
                    disabled={updating}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50"
                  >
                    Start Progress
                  </button>
                )}
                {ticket.status === 'in_progress' && (
                  <button
                    onClick={() => handleStatusChange('resolved')}
                    disabled={updating}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50"
                  >
                    Resolve
                  </button>
                )}
                {ticket.status === 'resolved' && (
                  <>
                    <button
                      onClick={() => handleStatusChange('closed')}
                      disabled={updating}
                      className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium disabled:opacity-50"
                    >
                      Close
                    </button>
                    <button
                      onClick={() => handleStatusChange('in_progress')}
                      disabled={updating}
                      className="w-full px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm font-medium disabled:opacity-50"
                    >
                      Reopen
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Assignment */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Assignment</h3>
              {ticket.assigned_to_name ? (
                <div className="flex items-center gap-3 mb-3">
                  <User className="w-8 h-8 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {ticket.assigned_to_name}
                    </p>
                    <p className="text-xs text-gray-500">
                      Assigned {new Date(ticket.assigned_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic mb-3">Unassigned</p>
              )}
              <button
                onClick={() => {/* TODO: Open assignment modal */}}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {ticket.assigned_to ? 'Reassign' : 'Assign to Me'}
              </button>
            </div>

            {/* Metadata */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Details</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>Created {new Date(ticket.created_at).toLocaleString()}</span>
                </div>
                {ticket.updated_at && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>Updated {new Date(ticket.updated_at).toLocaleString()}</span>
                  </div>
                )}
                {ticket.resolved_at && (
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span>Resolved {new Date(ticket.resolved_at).toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SupportTicketDetail;
